import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { MapPin } from "lucide-react";

/**
 * Lazy-loaded Google Street View thumbnail.
 *
 * Pass EITHER:
 *   • `leadId`  → fetches from `/api/leads/{leadId}/photos/street`
 *   • `address` → fetches from `/api/discover/street-view-thumb?address=...`
 *
 * Bytes are pulled once via `fetch()` with the Bearer token, then turned
 * into a blob object URL so the <img> tag can render without re-billing
 * Google. The backend cache (`property_photos` collection) means every
 * subsequent view across any user/session is free.
 *
 * Renders an inline placeholder while loading (skeleton) and a graceful
 * MapPin icon when the address has no Street View coverage.
 *
 * Props:
 *   address      — full street address (required when `leadId` not given)
 *   leadId       — owner-gated per-lead endpoint
 *   className    — Tailwind for the outer wrapper (sizing/rounding)
 *   alt          — img alt text
 *   onLoaded     — optional callback when the thumb loads successfully
 */
export default function StreetViewThumb({
  address,
  leadId,
  className = "w-full h-full",
  alt = "",
  onLoaded,
}) {
  const [src, setSrc] = useState(null);   // blob URL once loaded
  const [state, setState] = useState("loading"); // "loading" | "ok" | "empty"

  useEffect(() => {
    if (!address && !leadId) {
      setState("empty");
      return undefined;
    }
    const controller = new AbortController();
    let objUrl = null;

    (async () => {
      try {
        const token = localStorage.getItem("af_token");
        const url = leadId
          ? `${API}/leads/${leadId}/photos/street`
          : `${API}/discover/street-view-thumb?address=${encodeURIComponent(address)}`;
        const res = await fetch(url, {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });
        if (res.status === 204 || !res.ok) {
          setState("empty");
          return;
        }
        const blob = await res.blob();
        objUrl = URL.createObjectURL(blob);
        setSrc(objUrl);
        setState("ok");
        onLoaded?.();
      } catch (err) {
        if (err.name !== "AbortError") setState("empty");
      }
    })();

    return () => {
      controller.abort();
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [address, leadId, onLoaded]);

  if (state === "loading") {
    return (
      <div
        className={`${className} bg-slate-100 animate-pulse`}
        data-testid="street-view-thumb-loading"
      />
    );
  }
  if (state === "empty") {
    return (
      <div
        className={`${className} bg-slate-100 flex items-center justify-center`}
        title="No Street View coverage"
        data-testid="street-view-thumb-empty"
      >
        <MapPin size={14} className="text-[var(--ink-3)]" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`${className} object-cover`}
      data-testid="street-view-thumb-img"
    />
  );
}
